<script setup>
import { defineProps, defineEmits, ref, watch, toRef, computed } from 'vue';
import InputText from 'primevue/inputtext';
import FloatLabel from 'primevue/floatlabel';
import ColorPicker from 'primevue/colorpicker';

const props = defineProps({
    content: {
        type: String,
        required: true,
        default: ''
    },
    validation: {
        type: Array,
        default: () => []
    },
    element: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['update:content', 'changed', 'validated', 'unvalidated']);
const valueRef = ref('');
const valid = ref(true);
const invalidmessage = ref('');

const content = toRef(props, 'content');
watch(content, (newValue) => {
  valueRef.value = newValue;
});

const valueRefHash = computed({
  get() {
    return `#${valueRef.value}`
  },
  set(newValue) {
    // quitamos el # si lo tiene
    const match = newValue.match(/^#?([0-9a-fA-F]{6})$/)
    if (match) {
      // Convertimos a minúsculas y guardamos sin el #
      valueRef.value = match[1].toLowerCase()
    } else {
      valueRef.value = valueRef.value;
    }
  }
})

const validateRules = () => {
  let isValid = true;
  let value = valueRef.value;

  for (const rule of props.validation) {
    // Replace 'value' in the test expression with the actual value
    // Fix common typo in test expressions and ensure proper string handling
    const testExpression = rule.test
      .replace(/lenght/g, 'length');
    try {
      const func = new Function('value', `return ${testExpression}`);
      if (!func(value)) {
        isValid = false;
        emit('unvalidated', rule.message);
        invalidmessage.value = rule.message;
        break;
      }
    } catch (error) {
      console.error('Error evaluating validation rule:', error);
      isValid = false;
      emit('unvalidated', 'Error validating field');
      break;
    }
  }
  if (isValid) {
    valid.value = true;
    emit('validated');
  }
};

// Watch for value changes to trigger validation
watch(valueRef, (newValue, oldValue) => {
  if(newValue !== oldValue) {
    valid.value = false;
    validateRules();
    if (valid.value) {
      emit('update:content', newValue);
      emit('changed');
      invalidmessage.value = '';
    }
  }  
});

// Initialize value with content prop
valueRef.value = props.content;

</script>

<template>
  <div class="pb-1 pt-1">
    <div class="flex flex-row justify-content-between align-content-center">
      <div class="align-content-center">
        <div class="z-1 text-sm" style="text-overflow: ellipsis; white-space: nowrap;">
          {{ element.name }}
        </div>
      </div>

      <div>
        <InputText v-model="valueRefHash" size="small" 
          class="w-5rem mr-1 px-1 mono font-bold" />
        <ColorPicker v-model="valueRef" />
      </div>
    </div>
  </div>
  <div class="z-1 text-xs text-red-800 font-bold">
    {{ !valid ? invalidmessage : '' }}
  </div>
</template>

<style scoped>
.mono {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.8rem;
}
</style>